import React from 'react';

import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import PermissionsButtonWrapper from '~/components/core/Permissions/PermissionsButtonWrapper';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

interface FinanceVerificationBannerProps {
  hasPendingVerification: boolean;
  isApproved: boolean;
  requestingUser: string | null;
  requestDateTime: string | null;
  approvingUser: string | null;
  approvalDateTime: string | null;
  onApprove: () => void;
  onEdit: () => void;
  isApproving: boolean;
  isSelfRequest: boolean;
}

const FinanceVerificationBanner: React.FC<FinanceVerificationBannerProps> = ({
  hasPendingVerification,
  isApproved,
  requestingUser,
  requestDateTime,
  approvingUser,
  approvalDateTime,
  onApprove,
  onEdit,
  isApproving,
  isSelfRequest,
}) => {
  if (hasPendingVerification) {
    const formattedDate = requestDateTime ? serverDateTimeToLocal(requestDateTime) : null;
    const hasDetails = requestingUser && formattedDate;

    return (
      <AlertBanner
        alertType={AlertBanner.ALERT_TYPES.WARNING}
        withIcon
        title="Awaiting verification"
        note={
          <div className="flex items-center justify-between">
            {hasDetails && <span>{`Change submitted by ${requestingUser} on ${formattedDate}`}</span>}
            <div className="ml-auto flex gap-8">
              {isSelfRequest ? (
                <FsTooltip title="You cannot verify your own request">
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={onApprove}
                      disabled={isApproving || isSelfRequest}
                    >
                      Verify
                    </Button>
                  </span>
                </FsTooltip>
              ) : (
                <PermissionsButtonWrapper
                  action={PERMISSION_ACTIONS.CONTACT_FINANCE_APPROVER}
                  verb={PERMISSION_VERBS.WRITE}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={onApprove}
                    disabled={isApproving || isSelfRequest}
                  >
                    Verify
                  </Button>
                </PermissionsButtonWrapper>
              )}
              <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
                <Button variant="outlined" size="small" onClick={onEdit} className="bg-white">
                  Edit
                </Button>
              </PermissionsButtonWrapper>
            </div>
          </div>
        }
      />
    );
  }

  if (isApproved) {
    const formattedDate = approvalDateTime ? serverDateTimeToLocal(approvalDateTime) : null;
    const hasDetails = approvingUser && formattedDate;

    return (
      <AlertBanner
        alertType={AlertBanner.ALERT_TYPES.DONE}
        withIcon
        title="Verified"
        note={
          <div className="flex items-center justify-between">
            {hasDetails && <span>{`Change verified by ${approvingUser} on ${formattedDate}`}</span>}
            <div className="ml-auto">
              <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
                <Button variant="outlined" size="small" onClick={onEdit} className="bg-white">
                  Edit
                </Button>
              </PermissionsButtonWrapper>
            </div>
          </div>
        }
      />
    );
  }

  return null;
};

export default FinanceVerificationBanner;
